import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import * as pdfjsLib from "pdfjs-dist";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, Pencil, GripVertical, Save, Type, Hash, Calendar,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Search, Clock, Timer, ToggleLeft, AlignLeft, Image
} from "lucide-react";
import { toast } from "sonner";

// Set worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ─── Draggable/Resizable Variable Zone Component ──────────────────────────────
function VariableZone({ v, canvasDims, selected, onSelect, onUpdate, onDelete }) {
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const dragStart = useRef(null);

  const x = (v.x / 100) * canvasDims.width;
  const y = (v.y / 100) * canvasDims.height;
  const w = (v.width / 100) * canvasDims.width;
  const h = (v.height / 100) * canvasDims.height;

  const handleMouseDown = (e) => {
    if (e.target.closest('.zone-delete') || e.target.closest('.zone-resize')) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(v.id);
    setDragging(true);
    dragStart.current = { mx: e.clientX, my: e.clientY, ox: x, oy: y };
  };

  const handleResizeDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    dragStart.current = { mx: e.clientX, my: e.clientY, ow: w, oh: h, ox: x, oy: y };
  };

  useEffect(() => {
    if (!dragging && !resizing) return;

    const handleMouseMove = (e) => {
      if (dragging && dragStart.current) {
        const dx = e.clientX - dragStart.current.mx;
        const dy = e.clientY - dragStart.current.my;
        let newX = dragStart.current.ox + dx;
        let newY = dragStart.current.oy + dy;
        // Clamp within canvas
        newX = Math.max(0, Math.min(canvasDims.width - w, newX));
        newY = Math.max(0, Math.min(canvasDims.height - h, newY));
        const xPct = (newX / canvasDims.width) * 100;
        const yPct = (newY / canvasDims.height) * 100;
        onUpdate(v.id, { x: xPct, y: yPct });
      }
      if (resizing && dragStart.current) {
        const dx = e.clientX - dragStart.current.mx;
        const dy = e.clientY - dragStart.current.my;
        let newW = Math.max(30, dragStart.current.ow + dx);
        let newH = Math.max(16, dragStart.current.oh + dy);
        // Clamp to canvas bounds
        const currX = dragStart.current.ox;
        const currY = dragStart.current.oy;
        newW = Math.min(newW, canvasDims.width - currX);
        newH = Math.min(newH, canvasDims.height - currY);
        const wPct = (newW / canvasDims.width) * 100;
        const hPct = (newH / canvasDims.height) * 100;
        onUpdate(v.id, { width: wPct, height: hPct });
      }
    };

    const handleMouseUp = () => {
      setDragging(false);
      setResizing(false);
      dragStart.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, resizing, v.id, canvasDims, w, h, onUpdate]);

  return (
    <div
      className={`variable-zone ${selected ? "selected" : ""}`}
      style={{
        left: `${x}px`,
        top: `${y}px`,
        width: `${w}px`,
        height: `${h}px`,
      }}
      onMouseDown={handleMouseDown}
      onClick={() => onSelect(v.id)}
      data-testid={`var-zone-${v.id}`}
    >
      <span className="zone-label">{v.name}</span>
      <button
        className="zone-delete"
        onClick={(e) => { e.stopPropagation(); onDelete(v.id); }}
        data-testid={`var-zone-delete-${v.id}`}
      >
        <Trash2 size={10} />
      </button>
      {/* Resize handle */}
      <div
        className="zone-resize"
        onMouseDown={handleResizeDown}
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: 10,
          height: 10,
          cursor: "se-resize",
          background: "transparent",
        }}
      />
    </div>
  );
}

// ─── Main Editor Page ─────────────────────────────────────────────────────────
export default function TemplateEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authHeaders } = useAuth();

  const [template, setTemplate] = useState(null);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // PDF state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [canvasDims, setCanvasDims] = useState({ width: 0, height: 0 });
  const canvasRef = useRef(null);

  // Variable editing
  const [selectedVar, setSelectedVar] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newVarName, setNewVarName] = useState("");
  const [newVarType, setNewVarType] = useState("text");
  const [editVarName, setEditVarName] = useState("");
  const [editVarType, setEditVarType] = useState("text");
  const [editVarFontSize, setEditVarFontSize] = useState(12);
  const [searchQuery, setSearchQuery] = useState("");

  // Load template
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const res = await axios.get(`${API}/templates/${id}`, authHeaders());
        setTemplate(res.data);
        setVariables(res.data.variables || []);
      } catch {
        toast.error("Failed to load template");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, [id]);

  // Load PDF
  useEffect(() => {
    if (!template) return;
    const loadPdf = async () => {
      try {
        const res = await axios.get(`${API}/templates/${id}/pdf`, {
          ...authHeaders(),
          responseType: "arraybuffer",
        });
        const pdf = await pdfjsLib.getDocument({ data: res.data }).promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(0);
      } catch {
        toast.error("Failed to load PDF");
      }
    };
    loadPdf();
  }, [template]);

  // Render current page
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    const renderPage = async () => {
      const page = await pdfDoc.getPage(currentPage + 1);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      setCanvasDims({ width: viewport.width, height: viewport.height });
      await page.render({ canvasContext: ctx, viewport }).promise;
    };
    renderPage();
  }, [pdfDoc, currentPage, scale]);

  // Save variables
  const saveVariables = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/templates/${id}`, { variables }, authHeaders());
      toast.success("Variables saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Add variable
  const addVariable = () => {
    if (!newVarName.trim()) return;
    const exists = variables.some(v => v.name === newVarName.trim());
    if (exists) {
      toast.error("Variable name already exists");
      return;
    }
    const newVar = {
      id: crypto.randomUUID(),
      name: newVarName.trim(),
      type: newVarType,
      page: currentPage,
      x: 20,
      y: 10,
      width: 20,
      height: 3.5,
      font_size: 12,
    };
    setVariables([...variables, newVar]);
    setNewVarName("");
    setNewVarType("text");
    setAddDialogOpen(false);
    toast.success(`Variable "${newVar.name}" added`);
  };

  // Delete variable
  const deleteVariable = (varId) => {
    setVariables(variables.filter(v => v.id !== varId));
    if (selectedVar === varId) setSelectedVar(null);
  };

  // Update variable (for drag/resize)
  const updateVariable = useCallback((varId, updates) => {
    setVariables(prev => prev.map(v => v.id === varId ? { ...v, ...updates } : v));
  }, []);

  // Edit variable
  const openEditDialog = (v) => {
    setEditVarName(v.name);
    setEditVarType(v.type);
    setEditVarFontSize(v.font_size || 12);
    setSelectedVar(v.id);
    setEditDialogOpen(true);
  };

  const saveEditVariable = () => {
    setVariables(variables.map(v =>
      v.id === selectedVar
        ? { ...v, name: editVarName.trim(), type: editVarType, font_size: editVarFontSize }
        : v
    ));
    setEditDialogOpen(false);
    toast.success("Variable updated");
  };

  // Page variables
  const pageVariables = variables.filter(v => v.page === currentPage);
  const filteredVariables = searchQuery
    ? variables.filter(v => v.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : variables;

  const typeIcon = (type) => {
    if (type === "number") return <Hash size={12} strokeWidth={1.5} />;
    if (type === "date") return <Calendar size={12} strokeWidth={1.5} />;
    if (type === "time") return <Clock size={12} strokeWidth={1.5} />;
    if (type === "timestamp") return <Timer size={12} strokeWidth={1.5} />;
    if (type === "boolean") return <ToggleLeft size={12} strokeWidth={1.5} />;
    if (type === "textarea") return <AlignLeft size={12} strokeWidth={1.5} />;
    if (type === "image") return <Image size={12} strokeWidth={1.5} />;
    return <Type size={12} strokeWidth={1.5} />;
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-50" data-testid="template-editor-page">
      {/* Editor Header */}
      <header className="h-12 bg-white/80 backdrop-blur-md border-b border-slate-200/80 flex items-center justify-between px-4 flex-shrink-0 z-30" data-testid="editor-header">
        <div className="flex items-center gap-3">
          <Link to="/">
            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="back-button">
              <ArrowLeft size={16} strokeWidth={1.5} />
            </Button>
          </Link>
          <Separator orientation="vertical" className="h-5" />
          <h2 className="font-heading font-semibold text-sm truncate max-w-[300px]" data-testid="template-title">
            {template?.name}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 rounded-md px-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setScale(s => Math.max(0.5, s - 0.1))} data-testid="zoom-out-button">
              <ZoomOut size={14} strokeWidth={1.5} />
            </Button>
            <span className="text-xs font-mono w-10 text-center">{Math.round(scale * 100)}%</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setScale(s => Math.min(2.0, s + 0.1))} data-testid="zoom-in-button">
              <ZoomIn size={14} strokeWidth={1.5} />
            </Button>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-md px-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)} data-testid="prev-page-button">
              <ChevronLeft size={14} strokeWidth={1.5} />
            </Button>
            <span className="text-xs font-mono w-16 text-center">
              {currentPage + 1} / {totalPages}
            </span>
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)} data-testid="next-page-button">
              <ChevronRight size={14} strokeWidth={1.5} />
            </Button>
          </div>
          <Button size="sm" className="gap-1.5 h-8" onClick={saveVariables} disabled={saving} data-testid="save-button">
            <Save size={14} strokeWidth={1.5} />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* PDF Canvas Area */}
        <div className="flex-1 overflow-auto bg-[#525659] flex justify-center items-start p-8" data-testid="pdf-canvas-area">
          <div style={{ position: "relative", display: "inline-block", flexShrink: 0 }} className="shadow-lg ring-1 ring-black/10">
            <canvas ref={canvasRef} style={{ display: "block" }} data-testid="pdf-canvas" />
            {/* Variable Zones Overlay */}
            {canvasDims.width > 0 && pageVariables.map((v) => (
              <VariableZone
                key={v.id}
                v={v}
                canvasDims={canvasDims}
                selected={selectedVar === v.id}
                onSelect={setSelectedVar}
                onUpdate={updateVariable}
                onDelete={deleteVariable}
              />
            ))}
          </div>
        </div>

        {/* Right Sidebar */}
        <aside className="w-80 flex-shrink-0 border-l border-slate-200/80 bg-white flex flex-col" data-testid="variables-sidebar">
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-sm mb-1">Variables</h3>
            <p className="text-xs text-slate-500">
              Drag and resize zones on the PDF to position variables
            </p>
          </div>

          {/* Variable Types Quick Add */}
          <div className="p-3 border-b border-slate-100">
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { type: "text", icon: Type, label: "Text" },
                { type: "number", icon: Hash, label: "Number" },
                { type: "date", icon: Calendar, label: "Date" },
                { type: "time", icon: Clock, label: "Time" },
                { type: "timestamp", icon: Timer, label: "Timestamp" },
                { type: "boolean", icon: ToggleLeft, label: "Boolean" },
                { type: "textarea", icon: AlignLeft, label: "Text Area" },
                { type: "image", icon: Image, label: "Image" },
              ].map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.type}
                    className="flex flex-col items-center gap-1 p-2 rounded-md border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors text-xs text-slate-600"
                    onClick={() => { setNewVarType(t.type); setAddDialogOpen(true); }}
                    data-testid={`add-type-${t.type}`}
                  >
                    <Icon size={16} strokeWidth={1.5} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search & Add */}
          <div className="p-3 border-b border-slate-100 flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
              <Input
                placeholder="Search variables..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8 text-xs"
                data-testid="search-variables-input"
              />
            </div>
            <Button size="sm" className="gap-1 h-8 px-3" onClick={() => setAddDialogOpen(true)} data-testid="add-variable-button">
              <Plus size={14} strokeWidth={1.5} />
              Add
            </Button>
          </div>

          {/* Variable List */}
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-1.5" data-testid="variables-list">
              {filteredVariables.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-xs text-slate-400">No variables defined</p>
                  <p className="text-xs text-slate-400 mt-1">Click "Add" to create one</p>
                </div>
              ) : (
                filteredVariables.map((v) => (
                  <div
                    key={v.id}
                    className={`flex items-center gap-2 p-2.5 rounded-md border transition-colors cursor-pointer ${
                      selectedVar === v.id
                        ? "border-amber-400 bg-amber-50"
                        : "border-slate-100 hover:border-slate-200 hover:bg-slate-50"
                    }`}
                    onClick={() => {
                      setSelectedVar(v.id);
                      if (v.page !== currentPage) setCurrentPage(v.page);
                    }}
                    data-testid={`variable-item-${v.id}`}
                  >
                    <GripVertical size={12} className="text-slate-300 flex-shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {typeIcon(v.type)}
                        <span className="font-mono text-xs font-medium truncate">{v.name}</span>
                      </div>
                      <span className="text-[10px] text-slate-400">
                        Page {v.page + 1} | {v.type}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); openEditDialog(v); }} data-testid={`edit-var-${v.id}`}>
                        <Pencil size={12} strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); deleteVariable(v.id); }} data-testid={`delete-var-${v.id}`}>
                        <Trash2 size={12} strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Footer Info */}
          <div className="p-3 border-t border-slate-100 bg-slate-50/50">
            <p className="text-[10px] text-slate-400 text-center">
              {variables.length} variable{variables.length !== 1 ? "s" : ""} defined |
              Page {currentPage + 1} of {totalPages || 1}
            </p>
          </div>
        </aside>
      </div>

      {/* Add Variable Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Add Variable</DialogTitle>
            <DialogDescription>Create a new variable to place on the PDF template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Variable Name</Label>
              <Input
                value={newVarName}
                onChange={(e) => setNewVarName(e.target.value)}
                placeholder="e.g. customerName"
                data-testid="new-var-name-input"
                onKeyDown={(e) => e.key === "Enter" && addVariable()}
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newVarType} onValueChange={setNewVarType}>
                <SelectTrigger data-testid="new-var-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="time">Time</SelectItem>
                  <SelectItem value="timestamp">Timestamp</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  <SelectItem value="textarea">Text Area</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={addVariable} disabled={!newVarName.trim()} data-testid="confirm-add-variable">
              Add Variable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Variable Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Variable</DialogTitle>
            <DialogDescription>Modify the variable name, type, or font size.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Variable Name</Label>
              <Input
                value={editVarName}
                onChange={(e) => setEditVarName(e.target.value)}
                data-testid="edit-var-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={editVarType} onValueChange={setEditVarType}>
                <SelectTrigger data-testid="edit-var-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="time">Time</SelectItem>
                  <SelectItem value="timestamp">Timestamp</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  <SelectItem value="textarea">Text Area</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Font Size (pt)</Label>
              <Input
                type="number"
                value={editVarFontSize}
                onChange={(e) => setEditVarFontSize(parseInt(e.target.value) || 12)}
                min={6}
                max={72}
                data-testid="edit-var-fontsize-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEditVariable} disabled={!editVarName.trim()} data-testid="confirm-edit-variable">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
